/* =========================================================================
   pickup-points.js — Reblo pickup-point picker (InPost Paczkomat + ORLEN
   Paczka).

   Two render modes (set via [data-mode]):
     inline → checkbox in the cart page reveals an inline map.
     modal  → pill in the cart drawer opens a modal containing the map.
   Both modes share the same selection logic and write the chosen point to
   cart attributes ("Sposób dostawy", "Punkt odbioru", "Adres punktu",
   "_pickup_point_json"). The order's note_attributes carry them to fulfilment.

   "_pickup_point" is a DELIVERY-MODE enum — always exactly one of
   "normal" | "inpost" | "orlen": selectPoint sets inpost/orlen, the checkout
   button (chooseNormal) and "Usuń" (clearPoint) set "normal". The structured
   point JSON lives in "_pickup_point_json" so the worker's address swap keys
   off the mode enum but still has street/city/postcode.

   Tokens are read from window.theme.pickup (rendered by layout/theme.liquid
   from theme settings). When the optional helper-app URL is configured
   (window.theme.subscription.helperAppUrl), tokens from
   GET <helper>/api/pickup/config override the theme-settings fallback at
   runtime — same pattern as MisjaSucho.
   ========================================================================= */
(function () {
  if (window.customElements && customElements.get('pickup-points')) return;

  var _cache = {};
  function loadAsset(tag, attrs) {
    var key = tag + '|' + (attrs.src || attrs.href);
    if (_cache[key]) return _cache[key];
    _cache[key] = new Promise(function (resolve, reject) {
      var el = document.createElement(tag);
      Object.assign(el, attrs);
      el.onload = function () { resolve(); };
      el.onerror = function () { reject(new Error('load failed: ' + (attrs.src || attrs.href))); };
      document.head.appendChild(el);
    });
    return _cache[key];
  }

  var T = (window.theme && window.theme.pickup) || {};
  var I = T.inpost || {};
  var O = T.orlen || {};

  class PickupPoints extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.mode = this.dataset.mode || 'inline';

      this.checkbox  = this.querySelector('[data-pickup-checkbox]');
      this.body      = this.querySelector('[data-pickup-body]');
      this.picker    = this.querySelector('[data-pickup-picker]');
      this.summary   = this.querySelector('[data-pickup-summary]');
      this.mapBox    = this.querySelector('[data-pickup-map]');
      this.msgBox    = this.querySelector('[data-pickup-msg]');
      this.pill      = this.querySelector('[data-pickup-trigger]');
      this.modal     = this.querySelector('[data-pickup-modal]');
      this.checkoutBtn = document.querySelector('[data-checkout-btn]')
                     || document.querySelector('[data-checkout-link]');
      this.carrier   = (I.enabled ? 'inpost' : (O.enabled ? 'orlen' : 'inpost'));
      this.mounted   = {};

      try { this.point = JSON.parse(this.dataset.selected || 'null'); }
      catch (_) { this.point = null; }

      // Defaults from theme settings; helper-app config overrides at runtime.
      this.inpostEnabled = !!(I.enabled && (I.token || this.dataset.inpostToken));
      this.orlenEnabled  = !!(O.enabled  && (O.token  || this.dataset.orlenToken));
      this.inpostToken   = I.token || this.dataset.inpostToken || '';
      this.orlenToken    = O.token || this.dataset.orlenToken  || '';
      this.inpostSandbox = !!I.sandbox;
      this.inpostConfig  = this.dataset.inpostConfig || 'parcelcollect';
      this.orlenUrl      = O.widgetUrl || this.dataset.orlenUrl || '';
      this.anyCarrier    = this.inpostEnabled || this.orlenEnabled;

      var self = this;

      // Warm the map CDN connections now (from theme-settings tokens) so the
      // first map open is fast; loadConfig may add more once it resolves.
      this.preconnectMapHosts();

      // ----- inline mode wiring -----
      if (this.checkbox) this.checkbox.addEventListener('change', this.onToggle.bind(this));

      // ----- modal mode wiring (pill click + close handlers) -----
      if (this.pill) this.pill.addEventListener('click', function () { self.openModal(); });
      if (this.modal) {
        this.modal.querySelectorAll('[data-pickup-modal-close]').forEach(function (el) {
          el.addEventListener('click', function () { self.closeModal(); });
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && self.modal && !self.modal.hidden) self.closeModal();
        });
      }

      // ----- carrier tabs + change button (shared, attached BEFORE moving
      //       the modal to <body> so this.querySelectorAll still finds them) -----
      this.querySelectorAll('[data-carrier]').forEach(function (t) {
        t.addEventListener('click', function () { self.switchCarrier(t.dataset.carrier); });
      });
      var changeBtn = this.querySelector('[data-pickup-change]');
      if (changeBtn) changeBtn.addEventListener('click', function () { self.editPoint(); });
      // "Usuń" — clear the chosen point (pill + both selected bars).
      this.querySelectorAll('[data-pickup-remove]').forEach(function (b) {
        b.addEventListener('click', function (e) { e.preventDefault(); self.clearPoint(); });
      });

      // ----- escape the drawer's transform-containing-block -----
      // The cart drawer uses `transform: translateX(...)` for its slide-in
      // animation. CSS spec: an ancestor with `transform` becomes the
      // containing block for position:fixed descendants — which clips this
      // modal inside the 440-px drawer instead of centring on the viewport.
      // Move the modal node to <body> so it can size + centre against the
      // viewport at the configured 50/50 (desktop) or 90/90 (mobile) size.
      if (this.modal && this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
      }

      // ----- checkout gating + "Przejdź do kasy" = normal delivery (shared) -----
      if (this.checkoutBtn) {
        this.checkoutBtn.addEventListener('click', function (e) {
          if (self.checkoutBtn.getAttribute('aria-disabled') === 'true') {
            e.preventDefault(); e.stopPropagation(); return;
          }
          if (self._proceedNormal) return; // our own re-dispatch — let it through
          // Clicking the checkout button = the customer chose NORMAL (courier)
          // delivery: stamp _pickup_point="normal" and drop any stale parcel
          // selection BEFORE continuing to checkout. (Choosing a parcel point
          // goes through selectPoint, which stamps inpost/orlen + auto-redirects.)
          e.preventDefault();
          self.chooseNormal().then(function () { self._continueCheckout(); });
        });
        var form = this.checkoutBtn.form;
        if (form) form.addEventListener('submit', function (e) {
          if (self.checkoutBtn.getAttribute('aria-disabled') === 'true') e.preventDefault();
        });
      }

      this._inpostEvt = 'reblo-inpost-point';
      document.addEventListener(this._inpostEvt, function (e) { self.onInpostPoint(e.detail); });
      document.addEventListener('orlenSelectPoint', function (e) { self.onOrlenSelectPoint(e); }, true);

      this.configReady = this.loadConfig();

      // Inline mode: if the box is already ticked, reveal the picker right away.
      if (this.mode === 'inline' && this.checkbox && this.checkbox.checked) {
        if (this.body) this.body.removeAttribute('hidden');
        this.showPicker();
      }
      this.updateGate();
    }

    // ─── modal helpers ───────────────────────────────────────────────
    openModal() {
      if (!this.modal) return;
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('is-locked');
      // Always show the map immediately, even when a point is already
      // selected — the customer doesn't need to click "Zmień" first to
      // interact with the picker. The summary bar still shows the
      // current selection at the bottom for context.
      this.showPicker();
      if (this.point && this.summary) this.summary.removeAttribute('hidden');
      // Nudge the geowidget to re-measure now that the modal is visible and
      // has its final on-screen size. The InPost/Leaflet map sizes itself from
      // its container at mount time; if it mounted while the modal was hidden
      // (display:none) it reads 0×0 and renders tiny. Firing resize after a
      // couple of frames forces Leaflet's invalidateSize path.
      this.nudgeMapSize();
    }

    // The InPost geowidget (and Leaflet inside it) renders at its own intrinsic
    // height and will NOT grow to fill a flex/percentage container — so we
    // measure the real available space (from the map's top edge down to the
    // top of the gold "selected" bar / bottom of the card) and set an EXPLICIT
    // pixel height on both the map box and the widget host. Then we fire a
    // resize so Leaflet calls invalidateSize and repaints tiles to the new box.
    // Runs several times to cover the widget's async mount + the modal's
    // open transition, and is re-bound to window resize.
    fillMap() {
      if (!this.modal || this.modal.hidden || !this.mapBox) return;
      var card = this.modal.querySelector('.pickup-modal-card');
      if (!card) return;
      var cardRect = card.getBoundingClientRect();
      var mapRect = this.mapBox.getBoundingClientRect();
      // Bottom boundary = top of the gold summary bar if it's showing,
      // otherwise the card's bottom edge.
      var bottom = cardRect.bottom;
      var bar = card.querySelector('.pickup-selected');
      if (bar && !bar.hasAttribute('hidden') && bar.offsetParent !== null) {
        bottom = bar.getBoundingClientRect().top;
      }
      var avail = Math.max(280, Math.round(bottom - mapRect.top));
      this.mapBox.style.height = avail + 'px';
      this.mapBox.style.minHeight = avail + 'px';
      this.mapBox.querySelectorAll('inpost-geowidget, iframe, .pickup-geowidget').forEach(function (w) {
        w.style.height = avail + 'px';
        w.style.minHeight = avail + 'px';
      });
      // This synthetic resize (for Leaflet's invalidateSize) also hits our own
      // window-resize listener below — flag it so onResize skips the echo, or
      // fillMap → resize → fillMap recurses until the stack blows.
      this._selfResize = true;
      try { window.dispatchEvent(new Event('resize')); } catch (_) {}
      this._selfResize = false;
    }

    nudgeMapSize() {
      var self = this;
      // Run across the mount + open-transition window.
      [0, 80, 200, 450, 800, 1300].forEach(function (ms) {
        setTimeout(function () { self.fillMap(); }, ms);
      });
      // Keep it correct on viewport changes while the modal is open (once).
      if (!this._fillBound) {
        this._fillBound = true;
        var onResize = function () { if (!self._selfResize) self.fillMap(); };
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
      }
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      // Only release the scroll lock if no other overlay is open.
      var anyOpen = document.querySelector('.drawer[data-open="true"], .modal[data-open="true"]');
      if (!anyOpen) document.documentElement.classList.remove('is-locked');
    }

    showPicker() {
      if (this.summary) this.summary.setAttribute('hidden', '');
      if (this.picker)  this.picker.removeAttribute('hidden');
      this.mountCarrier(this.carrier);
    }

    // Warm the map CDNs (DNS + TLS) so the FIRST map open isn't a cold
    // handshake. Cheap, idempotent, and only for enabled carriers.
    preconnectMapHosts() {
      var hosts = [];
      if (this.inpostEnabled) {
        hosts.push(this.inpostSandbox
          ? 'https://sandbox-easy-geowidget-sdk.easypack24.net'
          : 'https://geowidget.inpost.pl');
      }
      if (this.orlenEnabled) hosts.push((this.orlenUrl || 'https://mapa.orlenpaczka.pl').replace(/\/+$/, ''));
      hosts.forEach(function (h) {
        if (document.head.querySelector('link[data-pickup-preconnect="' + h + '"]')) return;
        var l = document.createElement('link');
        l.rel = 'preconnect'; l.href = h; l.crossOrigin = 'anonymous';
        l.setAttribute('data-pickup-preconnect', h);
        document.head.appendChild(l);
      });
    }

    // ─── runtime config from Helper App ──────────────────────────────
    async loadConfig() {
      var helper = ((window.theme && window.theme.subscription && window.theme.subscription.helperAppUrl) || '').replace(/\/+$/, '');
      if (!helper) return;
      // Bounded: a slow/cold/unreachable helper must NOT stall the map, which
      // awaits configReady before mounting. On timeout we keep the theme-
      // settings tokens (the helper is only an override).
      var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, 4000) : null;
      try {
        var opts = { headers: { Accept: 'application/json' } };
        if (ctrl) opts.signal = ctrl.signal;
        var r = await fetch(helper + '/api/pickup/config', opts);
        if (timer) { clearTimeout(timer); timer = null; }
        if (!r.ok) return;
        var cfg = await r.json();
        if (cfg && cfg.enabled === false) { this.disableUi(); return; }
        if (cfg && cfg.inpost) {
          if (cfg.inpost.token) { this.inpostToken = cfg.inpost.token; this.inpostEnabled = true; }
          if (typeof cfg.inpost.sandbox === 'boolean') this.inpostSandbox = cfg.inpost.sandbox;
          if (cfg.inpost.config) this.inpostConfig = cfg.inpost.config;
        }
        if (cfg && cfg.orlen) {
          if (cfg.orlen.token) { this.orlenToken = cfg.orlen.token; this.orlenEnabled = true; }
          if (cfg.orlen.url) this.orlenUrl = cfg.orlen.url;
        }
        this.anyCarrier = this.inpostEnabled || this.orlenEnabled;
        this.preconnectMapHosts(); // helper may have just enabled a carrier
        this.updateGate();
      } catch (_) { /* keep theme-settings fallback (timeout / network / abort) */ }
      finally { if (timer) clearTimeout(timer); }
    }

    disableUi() {
      this.anyCarrier = false;
      this.style.display = 'none';
      if (this.checkbox) this.checkbox.checked = false;
      if (this.point) this.clearPoint();
      this.updateGate();
    }

    onToggle() {
      if (this.checkbox.checked) {
        if (this.body) this.body.removeAttribute('hidden');
        this.showPicker();
      } else {
        if (this.body) this.body.setAttribute('hidden', '');
        if (this.point) this.clearPoint();
      }
      this.updateGate();
    }

    switchCarrier(carrier) {
      this.carrier = carrier;
      // The modal (and the carrier tabs inside it) is reparented to <body> on
      // init, so this.querySelectorAll can't see them at click time — search
      // both roots (same trick as refreshDisplay) or the active-tab highlight
      // never follows the selected carrier.
      var roots = [this];
      if (this.modal && !this.contains(this.modal)) roots.push(this.modal);
      roots.forEach(function (root) {
        root.querySelectorAll('[data-carrier]').forEach(function (t) {
          t.classList.toggle('is-active', t.dataset.carrier === carrier);
        });
      });
      this.mountCarrier(carrier);
    }

    editPoint() { this.showPicker(); }

    showMsg(text) {
      if (!this.msgBox) return;
      this.msgBox.textContent = text || '';
      this.msgBox.hidden = !text;
    }

    async mountCarrier(carrier) {
      // Wait for the helper-app config, but never longer than ~3s — otherwise a
      // slow config resolve would keep the map from ever rendering. loadConfig
      // has its own 4s abort, but this guards the await site directly too.
      if (this.configReady) {
        try {
          await Promise.race([this.configReady, new Promise(function (r) { setTimeout(r, 3000); })]);
        } catch (_) {}
      }
      Object.entries(this.mounted).forEach(function (kv) { kv[1].hidden = kv[0] !== carrier; });
      this.showMsg('');
      if (this.mounted[carrier]) return;
      try {
        if (carrier === 'inpost') await this.mountInpost();
        else                      await this.mountOrlen();
      } catch (err) {
        this.showMsg(this.dataset.lUnavailable || 'Wybór punktu jest chwilowo niedostępny.');
      }
    }

    async mountInpost() {
      if (!this.inpostEnabled || !this.inpostToken) {
        this.showMsg(this.dataset.lUnavailable);
        throw new Error('inpost disabled or no token');
      }
      var host = this.inpostSandbox
        ? 'https://sandbox-easy-geowidget-sdk.easypack24.net'
        : 'https://geowidget.inpost.pl';
      await loadAsset('link',   { rel: 'stylesheet', href: host + '/inpost-geowidget.css' });
      await loadAsset('script', { src: host + '/inpost-geowidget.js', defer: true });
      var w = document.createElement('inpost-geowidget');
      w.setAttribute('token', this.inpostToken);
      w.setAttribute('language', (document.documentElement.lang || 'pl').slice(0, 2));
      w.setAttribute('config', this.inpostConfig || 'parcelcollect');
      w.setAttribute('onpoint', this._inpostEvt);
      w.className = 'pickup-geowidget';
      this.mapBox.appendChild(w);
      this.mounted.inpost = w;
      this.nudgeMapSize();
    }

    // ORLEN's "Widget Mapy ORLEN Paczka" is NOT an iframe/postMessage embed —
    // per their integration docs it's a <script src=".../widget.js?token=...">
    // that scans the page once on load for elements carrying
    // class="orlen-widget" and renders the picker into them. We use the
    // INLINE element mode with data-layout="tabs" (the same integration shape
    // as Macopedia's Magento module): the search bar, type tabs, list and map
    // render directly inside our modal — visible on the first click, with
    // ORLEN's own responsive layout putting the map NEXT TO the list when the
    // container is wide (hence the wide .pickup-modal-card). The earlier
    // zero-sized-Leaflet failure of inline mode came from mounting into a
    // hidden container; we mount only from the OPEN modal, and
    // fillMap()/nudgeMapSize() give the host explicit pixel dimensions plus a
    // window resize, which makes Leaflet size itself correctly.
    async mountOrlen() {
      if (!this.orlenEnabled || !this.orlenToken) {
        this.showMsg(this.dataset.lUnavailable);
        throw new Error('orlen disabled or no token');
      }
      var host = (this.orlenUrl || 'https://mapa.orlenpaczka.pl').replace(/\/+$/, '');
      // Hidden input the widget writes the chosen point id into (data-target).
      // Lives OUTSIDE the map box: .pickup-map > * forces display:block on
      // direct children, which would un-hide a type="hidden" input.
      var target = document.getElementById('reblo-orlen-point-id');
      if (!target) {
        target = document.createElement('input');
        target.type = 'hidden';
        target.id = 'reblo-orlen-point-id';
        this.mapBox.parentNode.insertBefore(target, this.mapBox);
      }
      // Fallback selection channel: widget.js also writes the chosen point id
      // into the data-target input. The rich orlenSelectPoint event (address
      // details) is the primary channel; if a widget version only fires a
      // change on the input, still register the selection from the bare code
      // so the cart attributes (_pickup_point "orlen" + the point JSON) get saved
      // and the worker's address swap keys off them. The short delay lets the
      // rich event win when both arrive.
      if (!target.dataset.rebloBound) {
        target.dataset.rebloBound = '1';
        var self = this;
        target.addEventListener('change', function () {
          var code = (target.value || '').trim();
          if (!code) return;
          setTimeout(function () {
            if (self.point && String(self.point.code) === code) return; // rich event already handled it
            self.selectPoint({ carrier: 'orlen', code: code, name: code, street: '', building: '', city: '', post_code: '', line1: '', line2: '' });
          }, 350);
        });
      }
      var w = document.createElement('div');
      w.className = 'orlen-widget pickup-geowidget pickup-orlen-inline';
      w.dataset.type = 'pickup';
      w.dataset.layout = 'tabs';
      w.dataset.target = '#reblo-orlen-point-id';
      this.mapBox.appendChild(w);
      this.mounted.orlen = w;
      // The element must already be in the document before widget.js runs,
      // since it scans for .orlen-widget elements once on load.
      await this.loadOrlenScript(host, this.orlenToken);
      this.nudgeMapSize();
    }

    loadOrlenScript(host, token) {
      if (this._orlenScriptPromise) return this._orlenScriptPromise;
      var src = host + '/widget.js?token=' + encodeURIComponent(token) +
        '&v=1.0.0&t=' + Math.floor(Date.now() / 1000);
      this._orlenScriptPromise = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.async = true;
        s.src = src;
        s.onload = function () { resolve(); };
        s.onerror = function () { reject(new Error('load failed: ' + src)); };
        document.head.appendChild(s);
      });
      return this._orlenScriptPromise;
    }

    // orlenSelectPoint is dispatched on the widget's own element and isn't
    // guaranteed to bubble. A capture-phase listener on document still sees
    // it on the way down regardless of the bubbles flag, so this works
    // without depending on widget.js internals. Bound once in connectedCallback.
    onOrlenSelectPoint(e) {
      if (this.carrier !== 'orlen') return;
      var opts = e && e.detail && e.detail.marker && e.detail.marker.options;
      if (!opts) return;
      var code = opts.id || opts.name;
      if (!code) return;
      this.selectPoint({
        carrier: 'orlen',
        code: String(code),
        name: opts.name || String(code),
        street: opts.addressLine || '',
        building: '',
        city: opts.city || '',
        post_code: opts.postalCode || '',
        line1: opts.addressLine || '',
        line2: ''
      });
    }

    onInpostPoint(d) {
      if (!d || this.carrier !== 'inpost') return;
      var det = d.address_details || {};
      this.selectPoint({
        carrier: 'inpost',
        code: d.name || '',
        name: d.name || '',
        street: det.street || '',
        building: det.building_number || '',
        city: det.city || '',
        post_code: det.post_code || '',
        line1: (d.address && d.address.line1) || '',
        line2: (d.address && d.address.line2) || ''
      });
    }

    async selectPoint(point) {
      this.point = point;
      var method = point.carrier === 'inpost' ? 'Paczkomat InPost' : 'Punkt ORLEN Paczka';
      var streetLine = [point.street, point.building].filter(Boolean).join(' ').trim();
      var cityLine = [point.post_code, point.city].filter(Boolean).join(' ').trim();
      var readable = [streetLine, cityLine].filter(Boolean).join(', ');
      // Delivery mode contract: `_pickup_point` is ALWAYS exactly one of
      // "normal" | "inpost" | "orlen". The structured point data (street, city,
      // post code, …) that the worker's address swap needs lives separately in
      // `_pickup_point_json` — so `_pickup_point` stays a clean enum.
      await this.save({
        'Sposób dostawy': method,
        'Punkt odbioru': point.code,
        'Adres punktu': readable,
        '_pickup_point': point.carrier === 'inpost' ? 'inpost' : 'orlen',
        '_pickup_point_json': JSON.stringify(point)
      });
      this.classList.add('has-point');
      this.refreshDisplay(method, point.code, readable);
      if (this.mode === 'modal') {
        // Attributes are persisted (await save above) → go to checkout NOW.
        // No artificial delay: the cart-attribute save is the only thing we
        // must wait for, and awaiting it above already guarantees the pickup
        // point is on the order by the time checkout loads.
        window.location.href = this.dataset.checkoutUrl || (window.routes && window.routes.cart_url + '/checkout') || '/checkout';
        return;
      } else if (this.picker && this.summary) {
        this.picker.setAttribute('hidden', '');
        this.summary.removeAttribute('hidden');
      }
      this.updateGate();
    }

    // Update every visible reference to the chosen point — both the pill
    // (modal mode) and the in-modal/inline summary panel. The modal is reparented
    // to <body> on open, so it's no longer a descendant of this element; search it
    // as an extra root or the modal's summary text would stay empty.
    refreshDisplay(method, code, readable) {
      var roots = [this];
      if (this.modal && !this.contains(this.modal)) roots.push(this.modal);
      function setAll(sel, text) {
        roots.forEach(function (root) {
          root.querySelectorAll(sel).forEach(function (el) { el.textContent = text; });
        });
      }
      setAll('[data-pickup-name]', method + ' ' + code);
      setAll('[data-pickup-addr]', readable);
      setAll('[data-pickup-summary-name]', method + ' ' + code);
      setAll('[data-pickup-summary-addr]', readable);
    }

    async clearPoint() {
      this.point = null;
      // Removing the point = back to normal courier delivery (not "unset").
      await this.save({
        'Sposób dostawy': '',
        'Punkt odbioru': '',
        'Adres punktu': '',
        '_pickup_point': 'normal',
        '_pickup_point_json': ''
      });
      this.classList.remove('has-point');
      // Reset the pill back to its idle prompt AND clear the modal's gold summary
      // bar. The modal is reparented to <body>, so search it as an extra root or
      // its text/visibility would stay stale (still showing the removed point).
      var idleTitle = this.getAttribute('data-l-pill-idle') || '';
      var idleSub = this.getAttribute('data-l-pill-idle-sub') || '';
      var roots = [this];
      if (this.modal && !this.contains(this.modal)) roots.push(this.modal);
      function setAll(sel, text) {
        roots.forEach(function (root) {
          root.querySelectorAll(sel).forEach(function (el) { el.textContent = text; });
        });
      }
      setAll('[data-pickup-name]', idleTitle);
      setAll('[data-pickup-addr]', idleSub);
      setAll('[data-pickup-summary-name]', '');
      setAll('[data-pickup-summary-addr]', '');
      roots.forEach(function (root) {
        root.querySelectorAll('[data-pickup-summary]').forEach(function (el) { el.setAttribute('hidden', ''); });
      });
      if (this.picker)  this.picker.removeAttribute('hidden');
      this.updateGate();
      // Clicking "Usuń" from the modal's gold summary bar should also dismiss the
      // popup (no-op for the inline cart-page picker / when the modal is closed).
      if (this.modal && !this.modal.hidden) this.closeModal();
    }

    // Set delivery mode to "normal" (courier) and drop any parcel selection.
    // Used by the checkout button. Resolves quickly and never blocks checkout —
    // even a slow/failed cart update yields after a short timeout.
    chooseNormal() {
      this.point = null;
      this.classList.remove('has-point');
      var save = this.save({
        'Sposób dostawy': '',
        'Punkt odbioru': '',
        'Adres punktu': '',
        '_pickup_point': 'normal',
        '_pickup_point_json': ''
      });
      var cap = new Promise(function (r) { setTimeout(r, 1200); });
      return Promise.race([save, cap]);
    }

    // Continue to checkout after chooseNormal(): follow the <a href> (drawer),
    // or re-submit the cart form with name="checkout" (cart page).
    _continueCheckout() {
      var btn = this.checkoutBtn;
      if (!btn) return;
      var href = btn.getAttribute('href');
      if (href) { window.location.href = href; return; }
      this._proceedNormal = true;
      if (btn.form && typeof btn.form.requestSubmit === 'function') {
        btn.form.requestSubmit(btn);
      } else {
        btn.click();
      }
    }

    save(attributes) {
      var url = this.dataset.cartUpdate || (window.theme && window.theme.cartUpdateUrl) || '/cart/update.js';
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ attributes: attributes })
      }).then(function () {
        document.dispatchEvent(new CustomEvent('reblo:cart:updated'));
      }).catch(function () { /* swallow */ });
    }

    updateGate() {
      if (!this.checkoutBtn) return;
      // Modal mode: "pickup mode is on" === any point chosen. Without a checkbox
      // we don't gate based on intent; the cart drawer pill is opt-in.
      // Inline mode (cart page): block when the box is checked but no point yet.
      var wantsPickup = this.mode === 'modal'
        ? false
        : !!(this.checkbox && this.checkbox.checked);
      var blocked = wantsPickup && this.anyCarrier && !this.point;
      this.checkoutBtn.disabled = false;
      this.checkoutBtn.setAttribute('aria-disabled', blocked ? 'true' : 'false');
      var msg = this.dataset.lRequired || 'Wybierz punkt odbioru.';
      if (blocked) {
        this.checkoutBtn.setAttribute('data-tooltip', msg);
        this.checkoutBtn.setAttribute('aria-label', msg);
      } else {
        this.checkoutBtn.removeAttribute('data-tooltip');
        this.checkoutBtn.removeAttribute('aria-label');
      }
      this.checkoutBtn.removeAttribute('title');
    }
  }
  customElements.define('pickup-points', PickupPoints);
})();
